import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { format } from "date-fns";
import { it } from "date-fns/locale";
import { FgLogo } from "@/components/FgLogo";
import { PartnerDocUpload, PartnerPaymentPanel } from "@/components/PartnerPayments";
import { BadgeCheck, CalendarDays, Check, Euro, Loader2, LogOut, MapPin, RefreshCw, Search, UserRound } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "fr_partner_token";

const formatDetail = (d) => {
  if (d == null) return "Errore di rete, riprova.";
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  if (typeof d?.msg === "string") return d.msg;
  return String(d);
};

const defaultPickup = () => {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  d.setHours(9, 0, 0, 0);
  return d;
};
const defaultDropoff = () => {
  const d = new Date();
  d.setDate(d.getDate() + 4);
  d.setHours(10, 0, 0, 0);
  return d;
};
const toLocalInput = (d) => format(d, "yyyy-MM-dd'T'HH:mm");

const Field = ({ label, ...props }) => (
  <label className="block">
    <span className="text-[10px] font-bold tracking-wider text-gray-400 uppercase">{label}</span>
    <input
      {...props}
      className="mt-1 w-full h-10 rounded-lg border border-gray-200 bg-gray-50 px-3 text-sm outline-none focus:border-fr-navy focus:bg-white transition-colors"
    />
  </label>
);

const StatusBadge = ({ status }) => (
  <span
    className={`text-[10px] font-bold rounded px-1.5 py-0.5 ${
      status === "confirmed" ? "bg-green-100 text-green-800" : status === "cancelled" ? "bg-red-100 text-fr-red" : "bg-amber-100 text-amber-800"
    }`}
  >
    {status.toUpperCase()}
  </span>
);

const AuthBox = ({ onLogin }) => {
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ business_name: "", contact_name: "", email: "", phone: "", password: "" });
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (mode === "login") {
        const { data } = await axios.post(`${API}/partner/login`, { email: form.email, password: form.password });
        localStorage.setItem(TOKEN_KEY, data.access_token);
        onLogin(data);
      } else {
        const { data } = await axios.post(`${API}/partner/register`, form);
        toast.success(data.note || "Richiesta inviata!");
        setMode("login");
      }
    } catch (err) {
      toast.error(formatDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto px-4 py-8">
      <div className="rounded-2xl bg-fr-navy text-white p-5 md:p-7" data-testid="partner-hero">
        <p className="text-[11px] font-extrabold tracking-wider uppercase text-white/60">Programma partner FabriRent</p>
        <h1 className="mt-1 text-2xl md:text-3xl font-extrabold tracking-tight">
          Vendi le nostre auto ai tuoi ospiti e guadagna il 10%
        </h1>
        <ul className="mt-4 space-y-2 text-sm text-white/85">
          <li className="flex gap-2"><BadgeCheck size={16} className="text-fr-red shrink-0 mt-0.5" /> Disponibilità auto in tempo reale, aggiornata dal gestionale</li>
          <li className="flex gap-2"><BadgeCheck size={16} className="text-fr-red shrink-0 mt-0.5" /> Prenoti per il cliente finale in 30 secondi: nome, telefono, date</li>
          <li className="flex gap-2"><BadgeCheck size={16} className="text-fr-red shrink-0 mt-0.5" /> Commissione fissa sul totale di ogni prenotazione confermata</li>
          <li className="flex gap-2"><BadgeCheck size={16} className="text-fr-red shrink-0 mt-0.5" /> Consegna gratuita da 5 a 90 minuti: hotel, B&B, aeroporto, stazioni</li>
        </ul>
      </div>

      <form onSubmit={submit} className="mt-6 bg-white rounded-2xl border border-gray-100 shadow-card p-5" data-testid="partner-auth-form">
        <p className="font-extrabold text-fr-navy">{mode === "login" ? "Accedi all'area partner" : "Registrati come partner (hotel, B&B, affitti brevi)"}</p>
        {mode === "register" && (
          <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label="Nome struttura *" value={form.business_name} onChange={set("business_name")} placeholder="Hotel Vesuvio" data-testid="partner-input-business" />
            <Field label="Referente *" value={form.contact_name} onChange={set("contact_name")} placeholder="Mario Rossi" data-testid="partner-input-contact" />
            <Field label="Telefono *" type="tel" value={form.phone} onChange={set("phone")} placeholder="+39 081 000 0000" data-testid="partner-input-phone" />
          </div>
        )}
        <div className={`mt-3 grid grid-cols-1 ${mode === "register" ? "" : "sm:grid-cols-2"} gap-3`}>
          <Field label="Email *" type="email" value={form.email} onChange={set("email")} placeholder="tu@hotelvesuvio.it" data-testid="partner-input-email" />
          <Field label="Password * (min 8)" type="password" value={form.password} onChange={set("password")} placeholder="••••••••" data-testid="partner-input-password" />
        </div>
        <button
          type="submit"
          disabled={loading}
          className="mt-4 w-full rounded-xl bg-fr-navy text-white font-bold text-sm py-3 hover:bg-fr-navydark transition-colors flex items-center justify-center gap-2 disabled:opacity-60"
          data-testid="partner-auth-submit"
        >
          {loading ? <Loader2 size={16} className="animate-spin" /> : <UserRound size={15} />}
          {mode === "login" ? "Accedi" : "Invia richiesta di collaborazione"}
        </button>
        <button
          type="button"
          onClick={() => setMode(mode === "login" ? "register" : "login")}
          className="w-full mt-3 text-xs font-semibold text-gray-500 hover:text-fr-navy underline underline-offset-2"
          data-testid="partner-auth-switch"
        >
          {mode === "login" ? "Non sei ancora partner? Registrati" : "Hai già un account? Accedi"}
        </button>
        <p className="mt-3 text-[10px] text-gray-400 text-center">La registrazione richiede l'approvazione del gestionale FabriRent.</p>
      </form>
      <a href="/" className="block mt-4 text-center text-[11px] text-gray-400 hover:text-fr-navy underline underline-offset-2" data-testid="partner-back-to-site">← Torna al sito</a>
    </div>
  );
};

const AvailabilityPanel = ({ token, locations, onCreated }) => {
  const [pickup, setPickup] = useState(defaultPickup());
  const [dropoff, setDropoff] = useState(defaultDropoff());
  const [locCode, setLocCode] = useState(locations[0]?.code || "NCE");
  const [cats, setCats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sell, setSell] = useState(null);
  const [form, setForm] = useState({ customer_first_name: "", customer_last_name: "", customer_phone: "", preferred_model: "", notes: "" });
  const [docs, setDocs] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (locations.length && !locations.find((l) => l.code === locCode)) setLocCode(locations[0].code);
  }, [locations]); // eslint-disable-line

  const searchAvail = async () => {
    if (dropoff <= pickup) {
      toast.error("La consegna deve essere dopo il ritiro");
      return;
    }
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/partner/availability`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { pickup_at: pickup.toISOString(), dropoff_at: dropoff.toISOString() },
      });
      setCats(data.categories);
    } catch (e) {
      toast.error(formatDetail(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const days = Math.max(1, Math.ceil((dropoff.getTime() - pickup.getTime()) / 86400000));

  const createBooking = async () => {
    if (!form.customer_first_name || !form.customer_last_name || form.customer_phone.length < 6) {
      toast.error("Compila nome, cognome e telefono del cliente");
      return;
    }
    setBusy(true);
    try {
      const { data } = await axios.post(
        `${API}/partner/bookings`,
        { ...form, category: sell.category, location_code: locCode, pickup_at: pickup.toISOString(), dropoff_at: dropoff.toISOString(), docs },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Prenotazione ${data.reference} inviata al gestionale!`);
      setSell(null);
      setDocs([]);
      setForm({ customer_first_name: "", customer_last_name: "", customer_phone: "", preferred_model: "", notes: "" });
      searchAvail();
      onCreated();
    } catch (e) {
      toast.error(formatDetail(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="bg-white rounded-2xl border border-gray-100 shadow-card p-5" data-testid="partner-availability">
      <div className="flex items-center justify-between gap-2">
        <p className="font-extrabold text-fr-navy flex items-center gap-2"><CalendarDays size={16} /> Disponibilità in tempo reale</p>
        <button onClick={searchAvail} className="h-8 rounded-lg bg-gray-100 text-gray-700 text-[11px] font-bold px-3 flex items-center gap-1 hover:bg-gray-200" data-testid="partner-availability-refresh">
          <RefreshCw size={12} /> Aggiorna
        </button>
      </div>
      <div className="mt-3 grid grid-cols-1 sm:grid-cols-3 gap-3">
        <Field label="Ritiro" type="datetime-local" value={toLocalInput(pickup)} onChange={(e) => e.target.value && setPickup(new Date(e.target.value))} data-testid="partner-pickup-input" />
        <Field label="Consegna" type="datetime-local" value={toLocalInput(dropoff)} onChange={(e) => e.target.value && setDropoff(new Date(e.target.value))} data-testid="partner-dropoff-input" />
        <label className="block">
          <span className="text-[10px] font-bold tracking-wider text-gray-400 uppercase">Consegna a</span>
          <select value={locCode} onChange={(e) => setLocCode(e.target.value)} className="mt-1 w-full h-10 rounded-lg border border-gray-200 bg-gray-50 px-3 text-sm outline-none focus:border-fr-navy" data-testid="partner-location-select">
            {locations.map((l) => (
              <option key={l.code} value={l.code}>
                {l.name}{l.delivery_min != null ? ` (consegna ${l.delivery_min}–${l.delivery_max} min)` : ""}
              </option>
            ))}
          </select>
        </label>
      </div>
      <button
        onClick={searchAvail}
        disabled={loading}
        className="mt-3 w-full rounded-xl bg-fr-navy text-white font-bold text-sm py-3 hover:bg-fr-navydark transition-colors flex items-center justify-center gap-2 disabled:opacity-60"
        data-testid="partner-availability-search"
      >
        {loading ? <Loader2 size={15} className="animate-spin" /> : <Search size={15} />} Cerca disponibilità • {days} giorni
      </button>

      {cats && (
        <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="partner-availability-grid">
          {cats.map((c) => (
            <div key={c.category} className="rounded-xl border border-gray-200 bg-gray-50/60 p-3.5 flex flex-col" data-testid={`partner-avail-${c.category.replace(/[^a-z]/gi, "").toLowerCase()}`}>
              <div className="flex items-center justify-between">
                <span className="rounded-md bg-fr-ink text-white text-[10px] font-extrabold px-2 py-1">{c.category}</span>
                <span className={`text-[10px] font-bold rounded px-1.5 py-0.5 ${c.available > 0 ? "bg-green-100 text-green-800" : "bg-red-100 text-fr-red"}`}>
                  {c.available > 0 ? `${c.available} disponibili` : "Non disponibile"}
                </span>
              </div>
              <div className="mt-2 flex-1">
                <p className="text-[11px] text-gray-500">{c.models.map((m) => `${m.brand} ${m.model}`).slice(0, 3).join(" • ")} o similare</p>
              </div>
              <div className="mt-2 rounded-lg bg-white border border-gray-100 p-2 text-[11px]">
                <p className="flex justify-between"><span className="text-gray-500">Prezzo cliente</span><b className="text-fr-navy">{c.total_price != null ? `€ ${c.total_price}` : "da gestionale"}</b></p>
                <p className="flex justify-between"><span className="text-gray-500">Il tuo guadagno (10%)</span><b className="text-green-700">{c.commission_amount != null ? `€ ${c.commission_amount}` : "—"}</b></p>
              </div>
              <button
                onClick={() => setSell(c)}
                disabled={c.available <= 0}
                className="mt-2.5 w-full rounded-lg bg-fr-ink text-white text-xs font-bold py-2.5 hover:bg-black transition-colors disabled:opacity-40"
                data-testid={`partner-sell-${c.category.replace(/[^a-z]/gi, "").toLowerCase()}`}
              >
                Vendi al cliente finale
              </button>
            </div>
          ))}
        </div>
      )}

      {sell && (
        <div className="mt-4 rounded-xl border border-fr-navy/30 bg-gray-50 p-4" data-testid="partner-sell-form">
          <div className="flex items-center justify-between">
            <p className="text-sm font-extrabold text-fr-navy">Prenoti {sell.category} per il tuo cliente</p>
            <button onClick={() => setSell(null)} className="text-[11px] font-bold text-gray-400 hover:text-fr-red" data-testid="partner-sell-close">Annulla</button>
          </div>
          <div className="mt-3 grid grid-cols-1 sm:grid-cols-3 gap-3">
            <Field label="Nome cliente *" value={form.customer_first_name} onChange={(e) => setForm({ ...form, customer_first_name: e.target.value })} placeholder="Mario" data-testid="partner-customer-first-name" />
            <Field label="Cognome cliente *" value={form.customer_last_name} onChange={(e) => setForm({ ...form, customer_last_name: e.target.value })} placeholder="Rossi" data-testid="partner-customer-last-name" />
            <Field label="Telefono cliente *" type="tel" value={form.customer_phone} onChange={(e) => setForm({ ...form, customer_phone: e.target.value })} placeholder="350 000 0000" data-testid="partner-customer-phone" />
            <Field label="Modello richiesto" value={form.preferred_model} onChange={(e) => setForm({ ...form, preferred_model: e.target.value })} placeholder="Opzionale: Fiat Panda" data-testid="partner-customer-model" />
            <Field label="Note (hotel, camera, volo…)" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Opzionale" data-testid="partner-customer-notes" />
            <PartnerDocUpload docs={docs} setDocs={setDocs} />
          </div>
          <button
            onClick={createBooking}
            disabled={busy}
            className="mt-3 w-full rounded-xl bg-fr-red text-white font-bold text-sm py-3 hover:bg-red-700 transition-colors flex items-center justify-center gap-2 disabled:opacity-60"
            data-testid="partner-sell-submit"
          >
            {busy ? <Loader2 size={15} className="animate-spin" /> : <Check size={15} />} Invia prenotazione al gestionale
          </button>
        </div>
      )}
    </section>
  );
};

const BookingsPanel = ({ bookings, pct }) => {
  const month = new Date().toISOString().slice(0, 7);
  const [st, setSt] = useState(null);
  useEffect(() => { axios.get(`${API}/partner/statement`, { params: { month }, headers: { Authorization: `Bearer ${localStorage.getItem("fr_partner_token")}` } }).then((r) => setSt(r.data)).catch(() => {}); }, [month]);
  return (
  <section className="bg-white rounded-2xl border border-gray-100 shadow-card p-5" data-testid="partner-bookings">
    <p className="font-extrabold text-fr-navy flex items-center gap-2"><Euro size={16} /> Le tue prenotazioni e guadagni</p>
    {st && (
      <div className="mt-2 rounded-xl bg-green-50 border border-green-100 p-3 text-[11px]" data-testid="partner-statement">
        <b>Estratto {month}</b> — prenotazioni: {st.bookings} • incassato (solo effettivo): €{st.incassato} • commissione maturata: <b className="text-green-700">€{st.commissione_maturata}</b>
        <span className="text-gray-400 block">La commissione matura solo sugli importi effettivamente incassati: le preautorizzazioni non contano.</span>
      </div>
    )}
    {bookings.length === 0 ? (
      <p className="text-xs text-gray-400 mt-2">Nessuna prenotazione ancora: cerca la disponibilità e vendi la prima auto ai tuoi ospiti.</p>
    ) : (
      <div className="mt-3 space-y-2" data-testid="partner-bookings-list">
        {bookings.map((b) => (
          <div key={b.reference} className="rounded-xl border border-gray-100 bg-gray-50 p-3 text-xs" data-testid={`partner-booking-${b.reference}`}>
            <div className="flex flex-wrap items-center gap-2">
              <span className="font-mono font-extrabold text-fr-navy">{b.reference}</span>
              <span className="text-[10px] font-bold bg-fr-ink text-white rounded px-1.5 py-0.5">{b.category}</span>
              <StatusBadge status={b.status} />
              <span className="ml-auto font-bold text-green-700" data-testid={`partner-booking-earning-${b.reference}`}>
                Guadagno: {b.commission_incassata != null && b.commission_incassata > 0 ? `€ ${b.commission_incassata} (su incassato)` : b.commission_amount != null ? `€ ${b.commission_amount} previsti` : `da gestionale (${pct}%)`}
              </span>
            </div>
            <p className="text-[11px] text-gray-600 mt-1">
              {b.customer.first_name} {b.customer.last_name} • {b.customer.phone} • {b.location_name}
            </p>
            <p className="text-[11px] text-gray-400 mt-0.5">
              {format(new Date(b.pickup_at), "d MMM HH:mm", { locale: it })} → {format(new Date(b.dropoff_at), "d MMM HH:mm", { locale: it })} • {b.days} giorni
              {b.total_price != null && <> • cliente paga: <b className="text-fr-navy">€ {b.total_price}</b></>}
            </p>
            <PartnerPaymentPanel booking={b} />
          </div>
        ))}
      </div>
    )}
  </section>
  );
};

const Dashboard = ({ onLogout }) => {
  const [token] = useState(() => localStorage.getItem(TOKEN_KEY));
  const [me, setMe] = useState(null);
  const [locations, setLocations] = useState([]);
  const [bookings, setBookings] = useState([]);

  const loadBookings = () => {
    axios.get(`${API}/partner/bookings`, { headers: { Authorization: `Bearer ${token}` } }).then(({ data }) => setBookings(data.bookings)).catch(() => {});
  };
  useEffect(() => {
    axios.get(`${API}/partner/me`, { headers: { Authorization: `Bearer ${token}` } }).then(({ data }) => setMe(data)).catch((e) => {
      toast.error(formatDetail(e.response?.data?.detail));
      if (e.response?.status === 401) onLogout();
    });
    axios.get(`${API}/locations`).then(({ data }) => setLocations(data.locations)).catch(() => {});
    loadBookings();
  }, []); // eslint-disable-line

  if (!me) {
    return <div className="p-10 flex justify-center"><Loader2 size={28} className="animate-spin text-fr-navy" /></div>;
  }

  return (
    <div className="max-w-5xl mx-auto px-4 py-6 space-y-5">
      <section className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="partner-stats">
        {[
          { label: "Prenotazioni", value: me.stats.bookings, icon: CalendarDays },
          { label: "Guadagno confermato", value: `€ ${me.stats.earned}`, icon: Euro },
          { label: "In attesa conferma", value: `€ ${me.stats.pending}`, icon: MapPin },
          { label: "Commissione", value: `${me.commission_pct}%`, icon: BadgeCheck },
        ].map((s) => (
          <div key={s.label} className="rounded-2xl bg-white border border-gray-100 shadow-card p-4">
            <s.icon size={16} className="text-fr-red" />
            <p className="mt-1.5 text-lg font-extrabold text-fr-navy leading-none">{s.value}</p>
            <p className="text-[10px] text-gray-400 font-bold uppercase tracking-wide mt-1">{s.label}</p>
          </div>
        ))}
      </section>
      <p className="text-xs text-gray-500 -mt-2" data-testid="partner-welcome">
        Benvenuto, <b>{me.partner.business_name}</b> ({me.partner.contact_name}) — ogni prenotazione confermata ti fa guadagnare il <b>{me.commission_pct}%</b> del totale.
      </p>
      <AvailabilityPanel token={token} locations={locations} onCreated={loadBookings} />
      <BookingsPanel bookings={bookings} pct={me.commission_pct} />
    </div>
  );
};

export default function PartnerPage() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY));
  const [suspended, setSuspended] = useState(false);
  // COMANDO SITO 4.2: flusso hotel/partner invariato ma SPENTO finché il titolare non decide
  useEffect(() => {
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/switches`).then((r) => r.json()).then((sw) => setSuspended(!sw.partner_flow)).catch(() => {});
  }, []);

  const onLogout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken(null);
  };

  return (
    <div className="min-h-screen bg-gray-50" data-testid="partner-page">
      <header className="bg-fr-navy text-white h-14 flex items-center px-4 sticky top-0 z-20">
        <a href="/" className="flex items-center gap-2.5" data-testid="partner-home-link">
          <FgLogo size={32} />
          <span className="font-extrabold tracking-tight">Area Partner FabriRent</span>
        </a>
        {token && (
          <button onClick={onLogout} className="ml-auto flex items-center gap-1.5 text-xs font-semibold bg-white/10 rounded-lg px-3 py-2 hover:bg-white/20" data-testid="partner-logout">
            <LogOut size={13} /> Esci
          </button>
        )}
      </header>
      {suspended && (
        <div className="bg-amber-100 border-b border-amber-300 text-amber-900 text-xs font-semibold px-4 py-2" data-testid="partner-suspended-banner">
          Servizio momentaneamente sospeso: per le disponibilità e le prenotazioni per i tuoi ospiti contatta il gestionale — 081 18351540 • 350 9656394
        </div>
      )}
      {token ? <Dashboard onLogout={onLogout} /> : <AuthBox onLogin={setToken} />}
    </div>
  );
}
